import { message } from 'antd';
import queryString from 'query-string';
import { all, call, put, takeEvery } from 'redux-saga/effects';

import * as hotelAdminActions from 'app/redux/actions/hotelAdmin';
import type { HotelAdminContact } from 'app/redux/models/Hotel/Hotel';
import { get, post, requestGenerator } from 'app/redux/requests';
import type { IHotel } from 'app/typings';

const BILLS_URL = 'admin/bills';

export function* getHotels() {
  yield requestGenerator(
    () => put(hotelAdminActions.startLoading()),
    () => get('hotels'),
    (result: IHotel[]) => put(hotelAdminActions.fetchHotelsSuccess({ result })),
    () =>
      all([
        put(hotelAdminActions.stopLoading()),
        call(message.error('Error fetching hotels')),
      ])
  );
}

export function* downloadBill({
  payload,
}: ReturnType<typeof hotelAdminActions.downloadBill>): Generator<
  unknown,
  unknown,
  unknown
> {
  const { billId, hotelId } = payload;

  const stringifiedParams = queryString.stringify({
    hotelId,
  });

  return yield requestGenerator(
    () => Promise.resolve(),
    () => get(`${BILLS_URL}/${billId}/pdf?${stringifiedParams}`),
    (result: Blob, response: Response) => {
      const headers = (response.headers.get('Content-Disposition') ?? '').split(
        '; '
      );

      const filename =
        headers.length === 3
          ? // Header Format: inline; filename="Staycation - Invoice F-202210-1 - VIP Paris Yacht H?tel.pdf"; filename*=utf-8''Staycation%20-%20Invoice%20F-202210-1%20-%20VIP%20Paris%20Yacht%20H%c3%b4tel.pdf
            headers[2].split("filename*=utf-8''")[1]
          : // Header Format: inline; filename="Staycation - Invoice F-202210-2 - Couvent des Minimes.pdf"
            headers[1].split('filename=')[1].replaceAll('"', '');

      return put(
        hotelAdminActions.downloadBillSuccess({
          pdf: result,
          filename: decodeURIComponent(filename),
        })
      );
    },
    () =>
      all([
        put(hotelAdminActions.downloadBillFailure()),
        message.error('Error downloading Bill'),
      ]),
    { expectFile: true }
  );
}

export function* getContacts(
  action: ReturnType<typeof hotelAdminActions.fetchContacts>
) {
  const { hotelId } = action.payload;

  yield requestGenerator(
    () => Promise.resolve,
    () => get(`hotel-admin/${hotelId}/contacts`),
    (res: HotelAdminContact) =>
      put(hotelAdminActions.fetchContactsSuccess({ res })),
    () => {
      put(hotelAdminActions.fetchContactsFailed());

      message.error(`Error fetching contacts for hotelId: ${hotelId}`);
    }
  );
}

export function* saveContacts(
  action: ReturnType<typeof hotelAdminActions.saveContacts>
) {
  const { hotelId, form } = action.payload;

  yield requestGenerator(
    () => Promise.resolve,
    () => post(`hotel-admin/${hotelId}/contacts`, form),
    (res: HotelAdminContact) => {
      return all([put(hotelAdminActions.saveContactsSuccess({ res }))]);
    },
    () => {
      put(hotelAdminActions.saveContactsFailed());

      message.error(`Error saving contacts for hotelId: ${hotelId}`);
    }
  );
}

export function* hotelAdminSagas() {
  yield all([
    takeEvery(hotelAdminActions.TRY_FETCH_HOTELS, getHotels),
    takeEvery(hotelAdminActions.HOTEL_ADMIN_BILLS_DOWNLOAD_BILL, downloadBill),
    takeEvery(hotelAdminActions.HOTEL_ADMIN_FETCH_CONTACTS, getContacts),
    takeEvery(hotelAdminActions.HOTEL_ADMIN_SAVE_CONTACTS, saveContacts),
  ]);
}
